import Util from "./util";
import CountdownController from "@/components/CountDown/CountdownController";
import type { CouponUIState, ActivityDetail, ActivityRule } from "@/types/models/coupon";
import type { GoodsDetail } from "@/types/api";
import { CartItem, PayType } from "@/types/store";
import { VoucherStatus } from "@/types/enum";
const VOUCHER_STATUS_NORMAL = VoucherStatus.Normal;
const VOUCHER_STATUS_USING = VoucherStatus.Using;
/**
 * 优惠券工具类
 * 提供优惠券排序、推荐、校验等相关功能
 */
export class CouponHelper {
  /**
   * 排序优惠券列表
   * @param couponList 优惠券数组
   * @returns 排序后的优惠券列表
   */
  static sortCouponList(couponList: CouponUIState[]) {
    // 初始化所有优惠券为未选中状态
    couponList.forEach((item) => (item.checked = false));

    // status状态: -1过期, 0正常, 1正在使用中, 5待领取, 10已使用
    const receivedStatus: VoucherStatus[] = [VOUCHER_STATUS_NORMAL, VOUCHER_STATUS_USING];
    //金额从大到小排序
    const receivedCouponList = couponList.filter((item) => receivedStatus.includes(item.status));

    // 检查优惠券是否可用（在有效期内且未禁用）
    const countdown = new CountdownController();
    receivedCouponList.forEach((item) => {
      const nowTime = countdown.getCurrentTime();
      const startDate = new Date(item.startDate).getTime();
      const endDate = new Date(item.endDate).getTime();
      item.disabled = nowTime > endDate || nowTime < startDate || !item.discountAmount;
    });

    return [
      ...receivedCouponList
        .filter((item) => !item.disabled)
        .sort(this.endDateValueSort)
        .sort(this.restrictValueSort)
        .sort(this.priceSortCompare),
      ...receivedCouponList
        .filter((item) => item.disabled)
        .sort(this.endDateValueSort)
        .sort(this.restrictValueSort)
        .sort(this.priceSortCompare),
    ];
  }

  /**
   * 获取推荐优惠券组合
   * @param originCouponList 原始优惠券列表
   * @param totalPrice 订单总金额
   * @param isVip 是否是VIP（暂未使用）
   * @param disCountPrice VIP折扣金额（暂未使用）
   * @returns 推荐使用的优惠券数组
   */
  static getRecommendCouponList(originCouponList: CouponUIState[], totalPrice: number, isVip: boolean = false, disCountPrice: number = 0): CouponUIState[] {
    let couponList = Util.deepCopy(originCouponList);

    // 过滤出可用优惠券
    couponList = couponList.filter((item) => (item.status === VOUCHER_STATUS_NORMAL || item.status === VOUCHER_STATUS_USING) && !item.disabled);

    if (!couponList.length) return [];

    // 筛选出可叠加优惠券，并针对有可能存在多次领取的相同的可叠加优惠券进行去重
    const isOverlayList = clearRepeatCoupon(couponList.filter((item) => item.isOverlay));

    // 计算可叠加优惠券总优惠金额
    const overlayTotalPrice = isOverlayList.reduce((total, coupon) => total + (coupon.discountAmount || 0), 0);

    // 获取不可叠加优惠券中可用的最大优惠
    const noOverlayMaxItem = couponList.filter((item) => !item.restrictValue && !item.disabled).sort((a, b) => (b.discountAmount || 0) - (a.discountAmount || 0))[0]; // 获得不可叠加无门槛减最多的一项
    const fullDiscount = couponList
      .filter((item) => totalPrice >= (item.restrictValue || 0) && !item.disabled)
      .sort((a, b) => (b.discountAmount || 0) - (a.discountAmount || 0))[0]; // 获得达到满减条件，减最多的一项

    // 确定最优的不可叠加优惠券
    let maxDiscountList: CouponUIState[] = [];
    if (!noOverlayMaxItem && fullDiscount) {
      maxDiscountList.push(fullDiscount);
    } else if (!fullDiscount && noOverlayMaxItem) {
      maxDiscountList.push(noOverlayMaxItem);
    } else if (fullDiscount && noOverlayMaxItem) {
      maxDiscountList.push((noOverlayMaxItem.discountAmount || 0) > (fullDiscount.discountAmount || 0) ? noOverlayMaxItem : fullDiscount);
    }

    // 比较可叠加优惠券总优惠金额和不可叠加优惠券的优惠金额，
    if (maxDiscountList.length) {
      maxDiscountList = overlayTotalPrice >= (maxDiscountList[0].discountAmount || 0) ? isOverlayList : maxDiscountList;
    } else {
      maxDiscountList = isOverlayList;
    }

    // 最终的推荐优惠券列表，再做一层过滤，防止总优惠金额大于商品总价
    const payTotalPrice = isVip ? totalPrice - disCountPrice : totalPrice; // 要付款金额
    const couponDiscountPrice = maxDiscountList.reduce((total, coupon) => total + (coupon.discountAmount || 0), 0);

    if (payTotalPrice <= couponDiscountPrice) {
      // 筛选出推荐优惠券所有的组合
      const subCouponList = this.getGroup(maxDiscountList, payTotalPrice);
      // 获取所有组合中优惠总价格最接近付款金额的组合
      maxDiscountList = this.getApproachArr(subCouponList, payTotalPrice).arr || [];
    }
    return maxDiscountList;
  }

  //给优惠券赋予优惠金额
  static getDiscountAmount(couponItem: CouponUIState, goodsList: { price: number }[]): number {
    const { restrictValue = 0, voucherValue = 0 } = couponItem;

    // 折扣券计算逻辑： 符合条件的商品总价*折扣
    if (couponItem.discountType === 1) {
      //需要叠加享受折扣券的商品总折扣价
      let totalPrice = 0;
      goodsList.forEach((goodsItem) => {
        //优惠券符合指定商品或 未指定商品
        if (this.isValidCouponForGoods(couponItem, goodsItem)) {
          if (goodsItem.hasOwnProperty("cartCount")) {
            totalPrice += goodsItem.price * ((goodsItem as CartItem).cartCount || 1);
          } else {
            totalPrice += goodsItem.price;
          }
        }
      });
      if (totalPrice >= restrictValue) {
        const { discountAmount: discount } = this.getDiscountCouponPrice(voucherValue, totalPrice);
        return discount || 0;
      } else {
        return 0;
      }
    } else {
      //满减券 查看符合券的商品总价是否符合满减条件，不符合返回0
      let totalPrice = 0;

      for (const goodsItem of goodsList) {
        if (this.isValidCouponForGoods(couponItem, goodsItem)) {
          if (goodsItem.hasOwnProperty("cartCount")) {
            totalPrice += goodsItem.price * ((goodsItem as CartItem).cartCount || 1);
          } else {
            totalPrice += goodsItem.price;
          }
          if (totalPrice >= restrictValue) {
            return voucherValue;
          }
        }
      }
      return 0;
    }
  }

  // 根据领取状态及价格排序优惠券，用于优惠券领取弹窗
  static couponStateSort(couponList: CouponUIState[]) {
    const receivedStatus = [0, 1];
    const receivedCouponList = couponList
      .filter((item) => receivedStatus.includes(item.status))
      .sort(this.endDateValueSort)
      .sort(this.restrictValueSort)
      .sort(this.priceSortCompare);
    const toReceiveCouponList = couponList
      .filter((item) => item.status === 5)
      .sort(this.endDateValueSort)
      .sort(this.restrictValueSort)
      .sort(this.priceSortCompare);

    return {
      sortCouponList: [...sortVip(toReceiveCouponList), ...sortVip(receivedCouponList)],
      receivedCouponList: sortVip(receivedCouponList),
      toReceiveCouponList: sortVip(toReceiveCouponList),
    };
  }

  //获取折扣券的优惠价格
  static getDiscountCouponPrice(discountValue: number, goodsPrice: number) {
    // 计算折扣金额
    const discountAmount = goodsPrice * ((10 - discountValue) / 10);
    // 计算实付金额
    const finalPrice = goodsPrice - discountAmount;
    // 四舍五入并格式化为最多保留两位小数
    const formattedDiscountAmount = Util.bankerRound(discountAmount * 100) / 100;
    const formattedFinalPrice = Util.bankerRound(finalPrice * 100) / 100;

    // 返回结果，格式化为只有两位小数
    return {
      discountAmount: Number(formattedDiscountAmount.toFixed(2)), // 优惠金额
      finalPrice: Number(formattedFinalPrice.toFixed(2)), // 实付金额
    };
  }

  //给优惠券赋予优惠金额(专门用于商品详情页，因为商品详情页需要无视满减门槛排序门槛)
  static getDiscountAmountForGoodDetail(couponItem, goodsList) {
    const { restrictValue, voucherValue } = couponItem;
    // 折扣券计算逻辑： 符合条件的商品总价*折扣
    if (couponItem.discountType === 1) {
      //需要叠加享受折扣券的商品总折扣价
      let totalPrice = 0;
      goodsList.forEach((goodsItem) => {
        //优惠券符合指定商品或 未指定商品
        if (this.isValidCouponForGoods(couponItem, goodsItem)) {
          totalPrice += goodsItem.price;
        }
      });
      const { discountAmount: discount } = this.getDiscountCouponPrice(voucherValue, totalPrice);
      return discount || 0;
    } else {
      return voucherValue;
    }
  }

  /**
   * 判断该商品是否符合优惠券使用条件
   * @param {*} couponItem 优惠券
   * @param {*} goodsItem 商品
   */
  static isValidCouponForGoods(couponItem: CouponUIState, goodsItem) {
    // 获取优惠券信息
    const { objectIDs, isExcludeObjectID = false } = couponItem;
    // 积分商品标记为不可使用优惠券
    if (goodsItem.integralPrice) return false;

    // 检查商品是否被标记为不可使用优惠券
    if (goodsItem.isNoAllowVoucher === 1) return false;

    // 如果优惠券没有指定商品范围，默认所有商品都可使用
    // 如果isExcludeObjectID为true, 则objectIDs不可为空
    if (!objectIDs) return true;

    const objectIDsArr = objectIDs.split(",").filter((id) => id !== "");

    const isInObjectIDs = objectIDsArr.includes((goodsItem.id || goodsItem.goodsID || "").toString());
    // 根据是否排除策略返回结果
    // 如果isExcludeObjectID为true，则objectIDs中的商品不可用优惠券
    // 如果isExcludeObjectID为false，则只有objectIDs中的商品可用优惠券
    return !!isExcludeObjectID !== isInObjectIDs;
  }

  private static getGroup(arr: CouponUIState[], target) {
    let res = [];
    let s = [];
    let maxSum = 0;
    for (let i = 0; i < arr.length; i++) {
      if (arr[i].discountAmount < target && arr[i].discountAmount > maxSum) {
        // s.shift() // 将之前加入的推出数组
        s.push({
          arr: [arr[i]],
          sum: arr[i].discountAmount,
        });
        maxSum = arr[i].discountAmount;
      }
      for (let j = 0; j < res.length; j++) {
        if (res[j].sum + arr[i].discountAmount < target && res[j].sum + arr[i].discountAmount > maxSum) {
          maxSum = res[j].sum + arr[i].discountAmount;
          // s.shift()
          s.push({
            arr: [...res[j].arr, ...[arr[i]]],
            sum: res[j].sum + arr[i].discountAmount,
          });
        }
      }
      res = [...s]; //防止爆堆内存，不要浅拷贝
    }
    return res;
  }

  private static getApproachArr(arrList, target) {
    if (!target) return [];
    if (arrList.length === 1) return arrList[0];
    let newArr = arrList.filter((item) => item.sum < target);
    if (newArr.length === 1) return newArr[0];
    let res = newArr.sort((a, b) => b.sum - a.sum)[0];
    return res || [];
  }

  // 有效期从短到长
  private static endDateValueSort(a: CouponUIState, b: CouponUIState): number {
    return new Date(a.endDate).getTime() - new Date(b.endDate).getTime();
  }
  // 按减免价格从高到低
  private static priceSortCompare(a: CouponUIState, b: CouponUIState): number {
    return (b.discountAmount || 0) - (a.discountAmount || 0);
  }
  // 按门槛从高到低
  private static restrictValueSort(a: CouponUIState, b: CouponUIState): number {
    return (b.restrictValue || 0) - (a.restrictValue || 0);
  }
}

function clearRepeatCoupon(arr) {
  let newArr = [];
  let obj = {};
  for (var i = 0; i < arr.length; i++) {
    if (!obj[arr[i].voucherID]) {
      newArr.push(arr[i]);
      obj[arr[i].voucherID] = true;
    }
  }
  return newArr;
}

// 按vip专享优惠券在前排序
// TODO 暂时关闭，无VIP排序
function sortVip(couponList) {
  return couponList;
  // return [...couponList.filter((item) => item.userTag === "VipUser"), ...couponList.filter((item) => item.userTag !== "VipUser")];
}

export default CouponHelper;
